import React from 'react'
import AdminSidebar from "../Sidebar/AdminSidebar";
import { AxiosResponse } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AdminSmsApi } from '../../apis/AdminSmsApi';
import { FaFileAlt, FaExternalLinkAlt } from 'react-icons/fa';

function NativeModal({ open, onClose, title, children }: {
  open: boolean; onClose: () => void; title: string; children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">&times;</button>
        </div>
        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

function getPageFromLink(url: string | null): string {
  if (!url) return '';
  try { return new URL(url).searchParams.get('page') ?? ''; } catch { return ''; }
}

function SenderIdManagement() {
    const [senderIds, setSenderIds] = React.useState<any>([]);
    const [searchText, setSearchText] = React.useState('');
    const [statusFilter, setStatusFilter] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    // Modal states
    const [visible, setVisible] = React.useState<boolean>(false);
    const [selectedSenderId, setSelectedSenderId] = React.useState<any>(null);
    const [actionType, setActionType] = React.useState<'approve' | 'reject'>('approve');
    const [rejectionReason, setRejectionReason] = React.useState('');

    const [docsOpen, setDocsOpen] = React.useState(false);

    React.useEffect(() => {
        fetchSenderIds();
    }, []);

    const fetchSenderIds = React.useCallback(() => {
        setLoading(true);
        const query: any = {
            search: searchText,
            status: statusFilter,
        };

        AdminSmsApi.getAllSenderIds('', query).then(
            (response: AxiosResponse<any>) => {
                if (response?.data) {
                    setSenderIds(response?.data);
                }
                setLoading(false);
            }
        ).catch(function (error: any) {
            console.log(error);
            setLoading(false);
        });
    }, [searchText, statusFilter]);

    const paginator = React.useCallback(
        (value: any) => {
            setLoading(true);
            let value2 = '';
            if (value !== null) {
                value2 = value;
            } else {
                value2 = ''
            }
            const query: any = {
                search: searchText,
                status: statusFilter,
            };

            AdminSmsApi.getAllSenderIds(value2, query).then(
                (response: AxiosResponse<any>) => {
                    if (response?.data) {
                        setSenderIds(response?.data);
                    }
                    setLoading(false);
                }
            ).catch(function (error: any) {
                console.log(error.response?.data);
                setLoading(false);
            });
        }, [searchText, statusFilter]);

    const openActionModal = (senderId: any, action: 'approve' | 'reject') => {
        setSelectedSenderId(senderId);
        setActionType(action);
        setRejectionReason('');
        setVisible(true);
    };

    const handleAction = () => {
        if (!selectedSenderId) return;

        const data: any = {
            status: actionType === 'approve' ? 'approved' : 'rejected'
        };

        if (actionType === 'reject' && rejectionReason.trim()) {
            data.rejection_reason = rejectionReason.trim();
        } else if (actionType === 'reject' && !rejectionReason.trim()) {
            toast.error('Please provide a rejection reason');
            return;
        }

        AdminSmsApi.updateSenderIdStatus(selectedSenderId.id, data).then(
            (response: any) => {
                if (response?.data) {
                    toast.success(`Sender ID ${actionType === 'approve' ? 'approved' : 'rejected'} successfully`);
                    setVisible(false);
                    fetchSenderIds(); // Refresh the list
                } else {
                    toast.error(response?.response?.data?.message || 'Action failed');
                }
            }
        ).catch(function (error: any) {
            console.log(error.response);
            toast.error(error.response?.data?.message || 'Action failed');
        });
    };

    const getStatusBadge = (status: string) => {
        const statusStyles = {
            pending: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800',
        };

        return (
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusStyles[status as keyof typeof statusStyles] || 'bg-gray-100 text-gray-800'}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    return (
        <>
            <AdminSidebar />
            <ToastContainer />

            <div className="relative md:ml-64 bg-white">
                <div className='py-10 lg:py-20 lg:px-10 px-6'>
                    <h1 className='text-[30px] font-semibold'>Sender ID Management</h1>

                    {/* Filters */}
                    <div className='flex flex-wrap justify-between items-center mt-6 mb-4'>
                        <div className="flex flex-wrap gap-4 items-center">
                            {/* Search */}
                            <div className="relative flex items-stretch">
                                <div className="absolute inset-y-0 right-2 flex items-center pl-3 pointer-events-none">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                                        <path fill="#9da4aa" d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0a5.5 5.5 0 0 1 11 0z" />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    value={searchText}
                                    onChange={e => setSearchText(e.target.value)}
                                    placeholder='Search by user or sender ID...'
                                    className="border border-gray-300 text-gray-500 text-sm rounded-md block w-64 pl-4 pr-10 p-2"
                                />
                            </div>

                            {/* Status Filter */}
                            <select
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                                className="border border-gray-300 text-gray-500 text-sm rounded-md px-3 py-2"
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </select>

                            <button
                                type='button'
                                onClick={fetchSenderIds}
                                disabled={loading}
                                className="font-normal text-white bg-[#0071BC] px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                {loading ? 'Loading...' : 'Search'}
                            </button>
                        </div>

                        {/* Stats */}
                        <div className="flex gap-4 text-sm">
                            <div className="bg-yellow-50 px-3 py-1 rounded">
                                Pending: {senderIds?.data?.filter((s: any) => s.status === 'pending').length || 0}
                            </div>
                            <div className="bg-green-50 px-3 py-1 rounded">
                                Approved: {senderIds?.data?.filter((s: any) => s.status === 'approved').length || 0}
                            </div>
                            <div className="bg-red-50 px-3 py-1 rounded">
                                Rejected: {senderIds?.data?.filter((s: any) => s.status === 'rejected').length || 0}
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="relative overflow-x-auto shadow-lg sm:rounded-lg mt-6">
                        <table className="w-full text-sm text-left rtl:text-right text-gray-500">
                            <thead className="text-xs text-gray-700 bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3">S/N</th>
                                    <th scope="col" className="px-6 py-3">User</th>
                                    <th scope="col" className="px-6 py-3">Sender ID</th>
                                    <th scope="col" className="px-6 py-3">Status</th>
                                    <th scope="col" className="px-6 py-3">Created Date</th>
                                    <th scope="col" className="px-6 py-3">Rejection Reason</th>
                                    <th scope="col" className="px-6 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-4 text-center">
                                            Loading sender IDs...
                                        </td>
                                    </tr>
                                ) : senderIds?.data?.length > 0 ? (
                                    senderIds.data.map((senderId: any, index: any) => (
                                        <tr key={senderId.id} className="bg-white border-b hover:bg-gray-50">
                                            <td className="px-6 py-4">{index + 1}</td>
                                            <td className="px-6 py-4">
                                                <div>
                                                    <div className="font-medium text-gray-900">{senderId.user?.name}</div>
                                                    <div className="text-gray-500 text-xs">{senderId.user?.email}</div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 font-medium">{senderId.sender_id}</td>
                                            <td className="px-6 py-4">{getStatusBadge(senderId.status)}</td>
                                            <td className="px-6 py-4">{new Date(senderId.created_at).toLocaleDateString()}</td>
                                            <td className="px-6 py-4">
                                                {senderId.rejection_reason ? (
                                                    <span className="text-red-600 text-xs">{senderId.rejection_reason}</span>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-1.5">
                                                    {senderId.documents && Object.keys(senderId.documents).length > 0 && (
                                                        <button
                                                            onClick={() => { setSelectedSenderId(senderId); setDocsOpen(true); }}
                                                            className="text-xs font-medium px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-1 w-fit"
                                                        >
                                                            <FaFileAlt size={10} /> View Docs
                                                        </button>
                                                    )}
                                                    {senderId.status === 'pending' && (
                                                        <div className="flex gap-1.5">
                                                            <button
                                                                onClick={() => openActionModal(senderId, 'approve')}
                                                                className="text-xs font-medium px-2 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100"
                                                            >
                                                                Approve
                                                            </button>
                                                            <button
                                                                onClick={() => openActionModal(senderId, 'reject')}
                                                                className="text-xs font-medium px-2 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100"
                                                            >
                                                                Reject
                                                            </button>
                                                        </div>
                                                    )}
                                                    {senderId.status === 'approved' && (
                                                        <button
                                                            onClick={() => openActionModal(senderId, 'reject')}
                                                            className="text-xs font-medium px-2 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100 w-fit"
                                                        >
                                                            Revoke
                                                        </button>
                                                    )}
                                                    {senderId.status === 'rejected' && (
                                                        <button
                                                            onClick={() => openActionModal(senderId, 'approve')}
                                                            className="text-xs font-medium px-2 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100 w-fit"
                                                        >
                                                            Approve
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-4 text-center">
                                            No sender IDs found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {senderIds?.links && (
                        <div className='m-4'>
                            {senderIds.links.map((data: any, index: any) => (
                                <button
                                    key={index}
                                    onClick={() => paginator(getPageFromLink(data?.url))}
                                    disabled={data?.active || !data?.url || loading}
                                    className={'mx-1 py-1 px-2 ' + (data?.active ? 'bg-[#0071BC] text-white' : 'bg-gray-300 text-black')}
                                >
                                    {data?.label === '&laquo; Previous' ? '< Prev' : data?.label === 'Next &raquo;' ? 'Next >' : data?.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Action Modal */}
            <NativeModal
                open={visible}
                onClose={() => setVisible(false)}
                title={`${actionType === 'approve' ? 'Approve' : 'Reject'} Sender ID`}
            >
                <p className="text-gray-600 mb-4">
                    Are you sure you want to {actionType} the sender ID "{selectedSenderId?.sender_id}"
                    for user {selectedSenderId?.user?.name}?
                </p>

                {actionType === 'reject' && (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Rejection Reason *
                        </label>
                        <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Please provide a reason for rejection..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={3}
                            required
                        />
                    </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                    <button
                        type="button"
                        onClick={() => setVisible(false)}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleAction}
                        className={`px-4 py-2 rounded-md text-white font-medium ${actionType === 'approve' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}
                    >
                        {actionType === 'approve' ? 'Approve' : 'Reject'} Sender ID
                    </button>
                </div>
            </NativeModal>

            {/* View Documents Modal */}
            <NativeModal
                open={docsOpen}
                onClose={() => setDocsOpen(false)}
                title={`Documents — ${selectedSenderId?.sender_id}`}
            >
                <div className="space-y-3 text-sm">
                    <p className="text-gray-500 text-xs">
                        User: <strong className="text-gray-700">{selectedSenderId?.user?.name}</strong> ({selectedSenderId?.user?.email})
                    </p>

                    {selectedSenderId?.documents?.cac_certificate && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium text-gray-700">CAC Certificate</span>
                            <a
                                href={selectedSenderId.documents.cac_certificate}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-xs font-medium"
                            >
                                <FaExternalLinkAlt size={10} /> View File
                            </a>
                        </div>
                    )}

                    {selectedSenderId?.documents?.financial_license && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium text-gray-700">Financial License</span>
                            <a
                                href={selectedSenderId.documents.financial_license}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-xs font-medium"
                            >
                                <FaExternalLinkAlt size={10} /> View File
                            </a>
                        </div>
                    )}

                    {selectedSenderId?.documents?.website_url && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium text-gray-700">Website URL</span>
                            <a
                                href={selectedSenderId.documents.website_url}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-xs font-medium"
                            >
                                <FaExternalLinkAlt size={10} /> {selectedSenderId.documents.website_url}
                            </a>
                        </div>
                    )}

                    {!selectedSenderId?.documents?.cac_certificate &&
                     !selectedSenderId?.documents?.financial_license &&
                     !selectedSenderId?.documents?.website_url && (
                        <p className="text-gray-400 text-center py-4">No documents submitted yet.</p>
                    )}

                    <div className="pt-2 flex justify-end gap-2">
                        {selectedSenderId?.status === 'pending' && (
                            <>
                                <button
                                    onClick={() => { setDocsOpen(false); openActionModal(selectedSenderId, 'reject'); }}
                                    className="px-3 py-1.5 text-xs font-medium rounded bg-red-50 text-red-600 hover:bg-red-100"
                                >
                                    Reject
                                </button>
                                <button
                                    onClick={() => { setDocsOpen(false); openActionModal(selectedSenderId, 'approve'); }}
                                    className="px-3 py-1.5 text-xs font-medium rounded bg-green-500 text-white hover:bg-green-600"
                                >
                                    Approve
                                </button>
                            </>
                        )}
                        {selectedSenderId?.status !== 'pending' && (
                            <button onClick={() => setDocsOpen(false)}
                                className="px-4 py-2 text-sm font-medium rounded bg-gray-100 text-gray-700 hover:bg-gray-200">
                                Close
                            </button>
                        )}
                    </div>
                </div>
            </NativeModal>
        </>
    )
}

export default SenderIdManagement